import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PlusSmIcon } from '@heroicons/react/outline';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import ToDoCard from './ToDoCard';

function computeOrderBetween(previousOrder, nextOrder) {
  const prev = Number.isFinite(previousOrder) ? previousOrder : null;
  const next = Number.isFinite(nextOrder) ? nextOrder : null;

  if (prev == null && next == null) return 1000;
  if (prev == null) return next - 1000;
  if (next == null) return prev + 1000;

  const mid = (prev + next) / 2;
  if (mid === prev || mid === next) return prev + 0.5;
  return mid;
}

function ToDoBoardView({
  todos,
  todosLoading,
  statuses,
  categories,
  users,
  updateTodo,
  createTodo,
  deleteTodo,
  moveTodo,
}) {
  const [draggingTodoId, setDraggingTodoId] = useState(null);

  const columns = useMemo(() => {
    const map = new Map();
    statuses.forEach((s) => map.set(s.id, []));
    todos.forEach((t) => {
      const arr = map.get(t.status) || [];
      arr.push(t);
      map.set(t.status, arr);
    });
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return Array.from(map.entries()).map(([statusId, items]) => ({ statusId, items }));
  }, [todos, statuses]);

  const statusById = useMemo(() => Object.fromEntries(statuses.map((s) => [s.id, s])), [statuses]);
  const categoryById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  useEffect(() => {
    return monitorForElements({
      onDragStart: ({ source }) => {
        const todoId = source.data?.todoId;
        if (typeof todoId === 'string') setDraggingTodoId(todoId);
      },
      onDrop: async ({ location, source }) => {
        setDraggingTodoId(null);
        const todoId = source.data?.todoId;
        const fromStatusId = source.data?.statusId;
        if (typeof todoId !== 'string') return;
        if (typeof fromStatusId !== 'string') return;

        const dropTargets = location.current.dropTargets;
        if (!dropTargets || dropTargets.length === 0) return;

        const cardTarget = dropTargets.find((t) => t.data?.kind === 'todo-card');
        const columnTarget = dropTargets.find((t) => t.data?.kind === 'todo-column');

        let destStatusId = null;
        let destIndex = null;

        if (cardTarget) {
          destStatusId = cardTarget.data.statusId;
          const edge = cardTarget.data.edge;
          const index = cardTarget.data.index;
          if (typeof destStatusId !== 'string') return;
          if (typeof index !== 'number') return;
          destIndex = index + (edge === 'bottom' ? 1 : 0);
        } else if (columnTarget) {
          destStatusId = columnTarget.data.statusId;
          if (typeof destStatusId !== 'string') return;
          const destItems = columns.find((c) => c.statusId === destStatusId)?.items || [];
          destIndex = destItems.length;
        } else {
          return;
        }

        const destinationItems = (columns.find((c) => c.statusId === destStatusId)?.items || []).filter(
          (t) => t.id !== todoId
        );
        const boundedIndex = Math.max(0, Math.min(destIndex, destinationItems.length));

        const prevOrder = destinationItems[boundedIndex - 1]?.order;
        const nextOrder = destinationItems[boundedIndex]?.order;
        const newOrder = computeOrderBetween(prevOrder, nextOrder);

        const doMove = moveTodo || ((id, patch) => updateTodo(id, patch));
        await doMove(todoId, { status: destStatusId, order: newOrder });
      },
    });
  }, [columns, moveTodo, updateTodo]);

  return (
    <div className="mt-6">
      <div className="flex gap-4 overflow-x-auto pb-2">
        {columns.map((col) => (
          <div key={col.statusId} className="min-w-[18rem] w-[18rem]">
            <ToDoBoardColumn statusId={col.statusId}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: statusById[col.statusId]?.color || '#94a3b8' }}
                  />
                  <span className="font-semibold truncate">
                    {statusById[col.statusId]?.label || col.statusId}
                  </span>
                  <span className="text-xs text-neutral-500">({col.items.length})</span>
                </div>
                <button
                  type="button"
                  className="p-2 rounded-lg hover:bg-slate-50"
                  title="Add"
                  onClick={() =>
                    createTodo({
                      name: 'New task',
                      description: '',
                      category: '',
                      assignee: null,
                      status: col.statusId,
                    })
                  }
                >
                  <PlusSmIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="mt-3 flex flex-col gap-2">
                {todosLoading ? (
                  <div className="text-sm text-neutral-500">Loading…</div>
                ) : col.items.length === 0 ? (
                  <div className="text-sm text-neutral-500">No tasks</div>
                ) : (
                  col.items.map((t, index) => (
                    <ToDoCard
                      key={t.id}
                      todo={t}
                      statusId={col.statusId}
                      index={index}
                      category={categoryById[t.category]}
                      assignee={userById[t.assignee]}
                      onDelete={deleteTodo}
                      isDragging={draggingTodoId === t.id}
                    />
                  ))
                )}
              </div>
            </ToDoBoardColumn>
          </div>
        ))}
      </div>
    </div>
  );
}

function ToDoBoardColumn({ statusId, children }) {
  const ref = useRef(null);
  const [isOver, setIsOver] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    return dropTargetForElements({
      element: el,
      canDrop: ({ source }) => source.data?.kind === 'todo-card',
      getData: () => ({ kind: 'todo-column', statusId }),
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false),
    });
  }, [statusId]);

  return (
    <div
      ref={ref}
      className={
        'bg-white shadow-basic rounded-lg border p-3 transition ' + (isOver ? 'ring-2 ring-slate-300' : '')
      }
    >
      {children}
    </div>
  );
}

export default ToDoBoardView;
